import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Download, Printer, AlertCircle } from 'lucide-react';
import { exportQuotationToPDF } from '@/lib/export';
import { QuotationPrintView } from '@/components/invoice/QuotationPrintView';
import type { Quotation } from '@/types/invoice';
import { SEO } from '@/components/SEO';
import { useAuth } from '@/hooks/useAuth';
import { Logo } from '@/components/ui/Logo';

export default function SharedQuotation() {
  const { userId, quotationId } = useParams<{ userId: string; quotationId: string }>();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSharedQuotation = async () => {
      if (!userId || !quotationId) {
        setError('Invalid URL link details.');
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, 'users', userId, 'quotations', quotationId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setQuotation({
            ...data,
            quotationId: docSnap.id,
            issueDate: data.issueDate?.toDate(),
            validUntil: data.validUntil?.toDate(),
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
          } as Quotation);
        } else {
          setError('The requested quotation was not found.');
        }
      } catch (err: any) {
        console.error('Error fetching shared quotation:', err);
        if (err.code === 'permission-denied') {
          setError('This document is not shared, or the owner does not have sharing enabled.');
        } else {
          setError('Failed to load shared quotation.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSharedQuotation();
  }, [userId, quotationId]);

  useEffect(() => {
    if (authLoading) return;
    if (!quotation || !userId || !quotationId) return;

    // Do not log view if the viewer is the creator/owner of the quotation
    if (user && user.uid === userId) {
      console.log('Bypassing view logging since the viewer is the quotation creator.');
      return;
    }

    const logQuotationView = async () => {
      try {
        const sessionKey = `quotation_viewed_${quotationId}`;
        const hasViewedInSession = sessionStorage.getItem(sessionKey) === 'true';
        if (hasViewedInSession) return;

        // Log view to the subcollection
        const viewRef = collection(db, 'users', userId, 'quotations', quotationId, 'views');
        await addDoc(viewRef, {
          viewedAt: new Date(),
          userAgent: navigator.userAgent,
          browser: getBrowserName(navigator.userAgent),
          os: getOSName(navigator.userAgent),
        });

        sessionStorage.setItem(sessionKey, 'true');
      } catch (err) {
        console.error('Failed to log quotation view:', err);
      }
    };

    logQuotationView();
  }, [quotation, userId, quotationId, user, authLoading]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!quotation) return;

    setDownloading(true);
    try {
      await exportQuotationToPDF(quotation, 'quotation-content');
      toast({ title: 'Success', description: 'Quotation downloaded successfully!' });
    } catch (err) {
      console.error('Download error:', err);
      toast({
        title: 'Download Failed',
        description: 'Failed to download quotation PDF.',
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !quotation) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
        <Card className="max-w-md w-full shadow-lg border-red-100">
          <CardContent className="py-12 text-center space-y-4">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 text-red-600">
              <AlertCircle className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-bold text-gray-950">Access Restricted</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {error || 'Unable to view this quotation.'}
            </p>
            <div className="pt-2 text-xs text-muted-foreground">
              Document direct links are a premium feature of QuickFreeInvoice Pro.
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // useEffect was moved up to follow rules of hooks

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <SEO
        title={quotation ? `Quotation ${quotation.quotationNumber || ''} - ${(quotation as any).from?.name || 'QuickFreeInvoice'}` : 'View Quotation - QuickFreeInvoice'}
        description={quotation ? `View quotation ${quotation.quotationNumber || ''} from ${(quotation as any).from?.name || 'issuer'} for ${quotation.billTo?.name || 'Client'}.` : 'Secure client quotation preview.'}
        noindex={true}
      />
      {/* Sticky top action bar for client */}
      <div className="sticky top-0 bg-white border-b print:hidden z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo />
            <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-0.5 rounded">Client View</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button size="sm" onClick={handleDownloadPDF} disabled={downloading}>
              <Download className="h-4 w-4 mr-2" />
              {downloading ? 'Downloading...' : 'Download PDF'}
            </Button>
          </div>
        </div>
      </div>

      {/* Render Document */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Security Advisory Disclaimer Banner */}
          <div className="bg-amber-50/80 border border-amber-200/80 rounded-lg p-4 text-left flex items-start gap-3 shadow-sm print:hidden">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <h5 className="font-heading font-semibold text-sm text-amber-900">Security Notice</h5>
              <p className="text-xs text-amber-800/90 mt-1 leading-relaxed">
                Always verify the identity of the sender and confirm quotation details directly with the issuer. 
                QuickFreeInvoice is a document generation platform; we do not verify issuer credentials or assume responsibility for any subsequent contracts or transactions.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto w-full">
            <div className="min-w-[600px] md:min-w-0">
              <QuotationPrintView quotation={quotation} elementId="quotation-content" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function getBrowserName(ua: string) {
  if (ua.includes('Chrome') && !ua.includes('Chromium') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome') && !ua.includes('Edg')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  return 'Other Browser';
}

function getOSName(ua: string) {
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Macintosh') || ua.includes('Mac OS')) return 'macOS';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('Linux')) return 'Linux';
  return 'Other OS';
}
